import { Decorative } from "@faire/design-tokens";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web--source/common/transform-images";
import { Button } from "@faire/web--source/slate/Button";
import { Opacity } from "@faire/web--source/slate/Color";
import { Column, HorizontalLine, Row } from "@faire/web--source/slate/Layout";
import { FaireLogo } from "@faire/web--source/slate/logos/FaireLogo";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { ResponsiveContentV2 } from "@faire/web--source/ui/ResponsiveContentV2";
import * as React from "react";
import styled from "styled-components";

import { LegalLinks } from "@faire/retailer/components/Footer/LegalLinks";
import { home } from "@faire/retailer/paths";

interface MakerSignUpWrapperProps {
  children?: React.ReactNode | undefined;
}

export const MakerSignUpWrapper: React.FC<MakerSignUpWrapperProps> = ({
  children,
}) => {
  const isSSR = useIsSSR();
  const BRAND_SIGN_UP_BACKGROUND_IMAGE = optimizeImageUrl(
    {
      url: "https://cdn.faire.com/fastly/a2433fe4b864a396996e79b48f5b39939f1353d65ea2cb4439823e87dc29e3eb.png",
    },
    isSSR
  );

  const backgroundImageUrl = BRAND_SIGN_UP_BACKGROUND_IMAGE;
  return (
    <Wrapper align="center">
      <FormWrapper>
        <ResponsiveContentV2
          desktopAndAbove={
            <ImageColumn
              $backgroundImageUrl={backgroundImageUrl}
              data-test-id="apply-page-image-column"
            >
              <AbsoluteButton variant="tertiary" linkTo={home()}>
                <FaireLogo size={100} />
              </AbsoluteButton>
            </ImageColumn>
          }
          mobile={null}
        />
        <ContentColumn
          align={{
            mobileAndAbove: "flex-start",
            tabletAndAbove: undefined,
          }}
          style={{
            maxWidth: "650px",
          }}
        >
          <ResponsiveContentV2
            mobile={
              <MobileLogoButton variant="tertiary" linkTo={home()}>
                <FaireLogo size={100} />
              </MobileLogoButton>
            }
          />
          <ContentWrapper>{children}</ContentWrapper>
          <ResponsiveContentV2
            tabletAndAbove={
              <div>
                <HorizontalLine />
                <Row style={{ padding: getSpacing("4x", 0, "4x", "4x") }}>
                  <LegalLinks />
                </Row>
              </div>
            }
          />
        </ContentColumn>
      </FormWrapper>
    </Wrapper>
  );
};

const Wrapper = styled(Column)`
  min-height: 100vh;
  background-color: ${Decorative.tint.neutral}${Opacity.O_50};
`;

const FormWrapper = styled(Row).attrs({ align: "flex-start", flex: 1 })`
  width: 100%;
`;

const ContentWrapper = styled(Column)`
  padding: ${getSpacing("9x", "10x")};
  @media (max-width: 1106px) {
    padding: ${getSpacing("9x", "5x")};
  }
  ${responsiveMediaQueries.xLargeAndAbove} {
    padding: 96px 104px;
  }
  ${responsiveMediaQueries.tablet} {
    padding: ${getSpacing("3x", "4x")};
  }
  ${responsiveMediaQueries.mobile} {
    padding: 0;
  }
`;

const ContentColumn = styled(Column).attrs({
  flex: 1,
  justify: "space-between",
})`
  width: 50vw;
  height: 100vh;
  overflow-y: auto;

  ${responsiveMediaQueries.mobile} {
    height: 100%;
    width: 100%;
  }
`;

const ImageColumn = styled(ContentColumn)<{
  $backgroundImageUrl?: string;
}>`
  width: 50vw;
  max-width: 50vw;
  height: 100vh;
  background-image: url(${(props) => props.$backgroundImageUrl});
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  position: relative;
`;

const AbsoluteButton = styled(Button)`
  position: absolute;
  top: 48px;
  left: 48px;
`;

const MobileLogoButton = styled(Button)`
  padding: ${getSpacing("3x", "3x", "1x")};
`;
